import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { addMonths, format } from "date-fns";
import { AlertTriangle, BellRing, ChevronLeft, ChevronRight, Clock, Download, FolderLock, Plus, Sparkles, Upload, ArrowRight } from "lucide-react";
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { toast } from "sonner";
import { api, downloadFile, errMsg } from "@/lib/api";
import { toDate, isoOf } from "@/lib/brand";
import { Card, EmptyState, PageHeader, ReminderRow, SkeletonRows } from "@/components/common";
import { MonthGrid, monthRange } from "@/components/MonthGrid";
import { useAuth } from "@/context/AuthContext";
import { VoiceButton } from "@/components/VoiceReminder";

const COLORS = ["#5439DC", "#EC1785", "#FF971F", "#1CA786", "#782FEA", "#FF5A60", "#F6AA3E", "#9A97AB"];

const Kpi = ({ icon: Icon, label, value, hint, tone, to, testid }) => (
  <Link to={to} className="pm-card p-5 flex items-center gap-4 hover:-translate-y-0.5 transition-transform" data-testid={testid}>
    <span className="w-12 h-12 rounded-2xl grid place-items-center" style={{ background: tone[0] }}><Icon className="w-5 h-5" style={{ color: tone[1] }} /></span>
    <span>
      <span className="block text-sm text-[var(--pm-muted)]">{label}</span>
      <span className="block text-3xl font-extrabold leading-tight" data-testid={`${testid}-value`}>{value}</span>
      <span className="block text-xs text-[var(--pm-muted)]">{hint}</span>
    </span>
  </Link>
);

function CalendarPreview({ today }) {
  const [cursor, setCursor] = useState(toDate(today));
  const [events, setEvents] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    const { start, end } = monthRange(cursor);
    api.get("/calendar", { params: { start: isoOf(start), end: isoOf(end) } }).then(({ data }) => setEvents(data)).catch(() => {});
  }, [cursor]);
  return (
    <Card title="Calendar" testid="dashboard-calendar" action={
      <div className="flex items-center gap-1">
        <span className="text-sm font-semibold mr-2">{format(cursor, "MMMM yyyy")}</span>
        <button className="p-1.5 rounded-lg hover:bg-[#F2EFFC]" onClick={() => setCursor(addMonths(cursor, -1))} aria-label="Previous month" data-testid="dash-cal-prev"><ChevronLeft className="w-4 h-4" /></button>
        <button className="p-1.5 rounded-lg hover:bg-[#F2EFFC]" onClick={() => setCursor(addMonths(cursor, 1))} aria-label="Next month" data-testid="dash-cal-next"><ChevronRight className="w-4 h-4" /></button>
      </div>}>
      <MonthGrid compact cursor={cursor} events={events} today={today} onSelect={(iso) => nav(`/calendar?date=${iso}`)} />
    </Card>
  );
}

function Onboarding({ onSeed }) {
  return (
    <div className="pm-card p-8 sm:p-12 relative overflow-hidden" data-testid="dashboard-onboarding">
      <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full blur-3xl opacity-25" style={{ background: "var(--pm-gradient)" }} />
      <p className="text-xs font-extrabold tracking-[0.16em] uppercase text-[#A0136E]">Welcome</p>
      <h2 className="text-3xl font-extrabold mt-2">Let's pin your first date.</h2>
      <p className="text-[var(--pm-muted)] mt-2 max-w-xl">Add a passport expiry, a birthday or a subscription renewal — your countdown starts instantly.</p>
      <div className="flex flex-wrap gap-3 mt-6 relative">
        <Link to="/reminders/new" className="pm-btn pm-btn-primary" data-testid="onboarding-add-btn"><Plus className="w-4 h-4" /> Add Reminder</Link>
        <Link to="/import-export" className="pm-btn pm-btn-outline" data-testid="onboarding-import-btn"><Upload className="w-4 h-4" /> Import Excel</Link>
        <button onClick={onSeed} className="pm-btn pm-btn-soft" data-testid="onboarding-demo-btn"><Sparkles className="w-4 h-4" /> Load demo data</button>
      </div>
      <p className="text-xs text-[var(--pm-muted)] mt-4">Demo data is clearly tagged “demo” and can be removed in one click from Settings.</p>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [win, setWin] = useState(user?.prefs?.due_soon_days || 30);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    api.get("/dashboard", { params: { window: win } }).then(({ data }) => setD(data)).catch((e) => setErr(errMsg(e)));
  }, [win]);
  useEffect(load, [load]);

  const seed = async () => {
    try { await api.post("/demo/seed"); toast.success("Demo reminders added"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const exportAll = () => downloadFile("/excel/export", { status: "active" }).catch(() => toast.error("Export failed"));
  const first = (user?.name || "").split(" ")[0];

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle={`${first ? `Hi ${first} — h` : "H"}ere's what's happening with your important dates.`} actions={<>
        <Link to="/import-export" className="pm-btn pm-btn-outline" data-testid="dash-import-btn"><Upload className="w-4 h-4" /> Import Excel</Link>
        <button onClick={exportAll} className="pm-btn pm-btn-outline" data-testid="dash-export-btn"><Download className="w-4 h-4" /> Export Excel</button>
        <VoiceButton testid="dash-voice-btn" />
        <Link to="/reminders/new" className="pm-btn pm-btn-primary" data-testid="dash-add-reminder-btn"><Plus className="w-4 h-4" /> Add Reminder</Link>
      </>} />
      {err && <p className="text-[var(--pm-danger)]" role="alert">{err} <button className="underline" onClick={load}>Retry</button></p>}
      {!d && !err && <SkeletonRows rows={6} />}
      {d && (
        <div className="space-y-6">
          {d.kpis.total === 0 && <Onboarding onSeed={seed} />}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 pm-fade-up">
            <Kpi icon={BellRing} label="Total Reminders" value={d.kpis.total} hint="Active dates you're tracking" tone={["#EFEBFF", "#5439DC"]} to="/reminders" testid="kpi-total" />
            <Kpi icon={AlertTriangle} label="Overdue" value={d.kpis.overdue} hint="Needs your attention" tone={["#FDECEE", "#E65363"]} to="/reminders?urgency=overdue" testid="kpi-overdue" />
            <Kpi icon={Clock} label="Due Soon" value={d.kpis.due_soon} hint={
              <span className="flex items-center gap-1">Within the next
                <select value={win} onClick={(e) => e.preventDefault()} onChange={(e) => setWin(Number(e.target.value))} className="bg-transparent font-semibold text-[var(--pm-primary)]" data-testid="kpi-window-select">
                  {[7, 30, 90].map((w) => <option key={w} value={w}>{w} days</option>)}
                </select></span>} tone={["#FEF3E2", "#C77A0A"]} to={`/reminders?urgency=due_soon,due_today`} testid="kpi-due-soon" />
            <Kpi icon={FolderLock} label="Documents" value={d.kpis.documents} hint="Files attached" tone={["#E6F1FF", "#2F6FDB"]} to="/documents" testid="kpi-documents" />
          </div>
          {d.overdue.length > 0 && (
            <div className="rounded-2xl border border-[#F6C9CF] bg-[#FFF5F6] p-4 flex flex-wrap items-center gap-3" data-testid="overdue-banner">
              <AlertTriangle className="w-5 h-5 text-[var(--pm-danger)]" />
              <p className="text-sm font-semibold flex-1">{d.overdue.length} reminder{d.overdue.length > 1 ? "s are" : " is"} overdue — {d.overdue.slice(0, 2).map((r) => r.title).join(", ")}{d.overdue.length > 2 ? "…" : ""}</p>
              <Link to="/reminders?urgency=overdue" className="pm-btn pm-btn-sm pm-btn-outline">Review</Link>
            </div>
          )}
          <div className="grid xl:grid-cols-[1.45fr_1fr] gap-6">
            <Card title="Upcoming Reminders" testid="upcoming-card" action={<Link to="/reminders" className="text-sm font-semibold text-[var(--pm-primary)] flex items-center gap-1">View all <ArrowRight className="w-4 h-4" /></Link>}>
              {d.upcoming.length ? d.upcoming.map((r) => <ReminderRow key={r.id} r={r} />) : <EmptyState title="Nothing upcoming" text="New reminders you add will appear here with a live countdown." />}
            </Card>
            <CalendarPreview today={d.today} />
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <Card title="Expiry Overview" testid="expiry-overview-card">
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={d.months}><XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} /><Tooltip cursor={{ fill: "#F6F4FD" }} />
                    <Bar dataKey="count" name="Reminders" radius={[8, 8, 0, 0]}>{d.months.map((m, i) => <Cell key={m.key} fill={COLORS[i % 3]} />)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-[var(--pm-muted)]">Next occurrences over the coming 6 months.</p>
            </Card>
            <Card title="Category Distribution" testid="category-card">
              {d.categories.length ? (
                <>
                  <div className="h-44 relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart><Pie data={d.categories} dataKey="count" nameKey="name" innerRadius={50} outerRadius={75} paddingAngle={2}>{d.categories.map((c, i) => <Cell key={c.name} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 grid place-items-center pointer-events-none"><span className="text-center"><span className="block text-2xl font-extrabold">{d.kpis.total}</span><span className="text-xs text-[var(--pm-muted)]">Reminders</span></span></div>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3">{d.categories.slice(0, 6).map((c, i) => <span key={c.name} className="text-xs text-[var(--pm-muted)] flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{c.name} ({c.count})</span>)}</div>
                </>
              ) : <EmptyState title="No categories yet" />}
            </Card>
            <Card title="Renewal Center" testid="renewal-card">
              {d.renewals.length ? d.renewals.map((r) => (
                <Link key={r.id} to={`/reminders/${r.id}`} className="block py-3 border-b border-[var(--pm-border)] last:border-0">
                  <p className="font-semibold">{r.title}</p><p className="text-xs text-[var(--pm-muted)]">{r.done} of {r.total} steps completed</p>
                </Link>
              )) : <p className="text-sm text-[var(--pm-muted)]">No renewals in progress.</p>}
              {d.upcoming_costs.length > 0 && (
                <div className="mt-4 rounded-xl bg-[#F6F4FD] p-3" data-testid="upcoming-costs">
                  <p className="text-xs font-semibold text-[var(--pm-muted)]">Estimated renewal costs · next 90 days</p>
                  {d.upcoming_costs.map((c) => <p key={c.currency} className="font-bold">{c.currency} {c.amount.toLocaleString()}</p>)}
                </div>
              )}
              <Link to="/renewals" className="pm-btn pm-btn-soft mt-4" data-testid="dash-view-renewals">View renewals <ArrowRight className="w-4 h-4" /></Link>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
